import type { Language } from "@templestay/i18n";

const LANGS: Language[] = ["ko", "en", "zh", "ja"];

function isFilled(v: unknown): boolean {
  return typeof v === "string" && v.trim().length > 0;
}

/**
 * i18n JSON 문자열 + 체크할 field 키 배열로
 * 언어별 완성도(0~100)와 전체 평균 반환.
 * 공백 전용 값은 미입력으로 취급.
 */
export function computeI18nCompletion(
  i18nStr: string,
  fields: string[]
): { byLang: Record<Language, number>; overall: number } {
  if (fields.length === 0) return { byLang: { ko: 0, en: 0, zh: 0, ja: 0 }, overall: 0 };

  let parsed: Record<Language, Record<string, unknown>> = { ko: {}, en: {}, zh: {}, ja: {} };
  try {
    parsed = JSON.parse(i18nStr) as typeof parsed;
  } catch {
    // ignore parse error
  }

  const byLang = {} as Record<Language, number>;
  let totalFilled = 0;
  const totalPossible = LANGS.length * fields.length;

  for (const lang of LANGS) {
    const obj = parsed[lang] ?? {};
    const filled = fields.filter((f) => isFilled(obj[f])).length;
    byLang[lang] = fields.length > 0 ? Math.round((filled / fields.length) * 100) : 0;
    totalFilled += filled;
  }

  const overall = totalPossible > 0 ? Math.round((totalFilled / totalPossible) * 100) : 0;
  return { byLang, overall };
}

/** name_i18n JSON (title 필드만) 기준 전체 완성도 % */
export function nameI18nCompletion(nameI18nStr: string): number {
  return computeI18nCompletion(nameI18nStr, ["title"]).overall;
}

/** 여러 i18n JSON 문자열의 모든 field 기준 종합 완성도 % */
export function aggregateCompletion(
  items: Array<{ i18nStr: string; fields: string[] }>
): number {
  let totalFilled = 0;
  let totalPossible = 0;

  for (const { i18nStr, fields } of items) {
    if (fields.length === 0) continue;
    let parsed: Record<Language, Record<string, unknown>> = { ko: {}, en: {}, zh: {}, ja: {} };
    try { parsed = JSON.parse(i18nStr) as typeof parsed; } catch { /* ignore */ }
    for (const lang of LANGS) {
      const obj = parsed[lang] ?? {};
      totalFilled += fields.filter((f) => isFilled(obj[f])).length;
      totalPossible += fields.length;
    }
  }

  return totalPossible > 0 ? Math.round((totalFilled / totalPossible) * 100) : 0;
}

/** i18n 객체에서 특정 lang + field가 채워졌는지 확인 */
export function isI18nFieldFilled(
  i18nStr: string,
  lang: Language,
  field: string
): boolean {
  try {
    const parsed = JSON.parse(i18nStr) as Record<Language, Record<string, unknown>>;
    return isFilled(parsed[lang]?.[field]);
  } catch {
    return false;
  }
}
